import { type FC, useMemo } from 'react';
import {
  BaselineIcon,
  Binary,
  Calendar,
  CalendarClock,
  DecimalsArrowRight,
  File,
  LetterText,
  ListChecks,
  ListTodo,
  RectangleEllipsis,
  ToggleRight,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { type FieldType, type MetadataField, metadataTypeOptions } from '@/types/metadata';
import { ManagementLayout } from '@/components/layouts/management-layout';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useGeneratePageTitle } from '@/hooks/data/useGeneratePageTitle';
import { type ColumnConfig } from '@/components/layouts/gallery-layout';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { buildColumns } from '@/components/data-table/build-columns';
import { useMetadataList } from '@/hooks/metadata/useMetadata';
import { deleteMetadata } from '@/services/metadata.service';
import { DamLayout } from '@/components/layouts/layout';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { ColumnType } from '@/types/column';
import { EntityType } from '@/types/entity';
import { QueryKey } from '@/lib/queryKeys';
import { GalleryView } from '@/types';
import { useTranslations } from 'next-intl';

const typeIconMap: Record<
  Exclude<FieldType, 'linked_assets' | 'slider' | 'dropzone' | 'quality_slider' | 'tag_selector'>,
  JSX.Element
> = {
  string: <BaselineIcon className="w-full" />,
  text: <LetterText className="w-full" />,
  integer: <Binary className="w-full" />,
  float: <DecimalsArrowRight className="w-full" />,
  boolean: <ToggleRight className="w-full" />,
  date: <Calendar className="w-full" />,
  datetime: <CalendarClock className="w-full" />,
  select: <ListTodo className="w-full" />,
  multi_select: <ListChecks className="w-full" />,
  file: <File className="w-full" />,
};

export const typeOptions = metadataTypeOptions
  .filter(({ value }) => value !== 'linked_assets') // Exclude linked assets type
  .map(({ label, value }) => ({
    value,
    label: (
      <div className="flex items-center gap-x-2">
        <div className="w-[16px]">
          {
            typeIconMap[
              value as Exclude<FieldType, 'linked_assets' | 'slider' | 'dropzone' | 'quality_slider' | 'tag_selector'>
            ]
          }
        </div>
        {label}
      </div>
    ),
  }));

const Metadata: FC = () => {
  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const queryClient = useQueryClient();
  const openModal = useOpenModal();

  const { toast } = useToast();
  const { generatePageTitle } = useGeneratePageTitle();

  const tEmptyStates = useTranslations('emptyStates');

  const configs: Array<ColumnConfig<MetadataField>> = useMemo(
    () => [
      {
        type: ColumnType.Select,
        opts: {
          accessorKey: 'id',
        },
      },
      {
        type: ColumnType.Text,
        opts: { id: 'name', accessorKey: 'name', label: 'Name', isMainColumn: true },
      },
      {
        type: ColumnType.Type,
        opts: {
          id: 'type',
          accessorKey: 'type',
          label: 'Type',
          galleryView: GalleryView.LIST,
          cellClassName: 'text-left',
        },
      },
      {
        type: ColumnType.Status,
        opts: {
          id: 'facet',
          accessorKey: 'facet',
          label: 'Facet',
          headerClassName: 'text-center',
        },
      },
      {
        type: ColumnType.Status,
        opts: {
          id: 'searchable',
          accessorKey: 'searchable',
          label: 'Searchable',
          headerClassName: 'text-center',
        },
      },
      {
        type: ColumnType.Status,
        opts: {
          id: 'public',
          accessorKey: 'public',
          label: 'Public',
          headerClassName: 'text-center',
        },
      },
    ],
    [],
  );

  const columns: Array<ColumnDef<MetadataField>> = useMemo(() => buildColumns(configs), [configs]);

  const deleteMetadataWithAuth = useAuthenticatedQueryFn(deleteMetadata);

  const deleteMetadataMutation = useMutation({
    mutationFn: async ({ id }: { id: string; onConfirm?: () => void }) => {
      await deleteMetadataWithAuth({ id });
    },
    onSuccess: (_data, { onConfirm }) => {
      toast({
        title: tToasts('deleteMetadataMutation.title'),
        description: tToasts('deleteMetadataMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.MetadataList] });

      onConfirm?.();
    },
    onError: (err) => {
      errorLog('Action failed:', err);

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  return (
    <>
      {generatePageTitle([tCommon('metadata')])}
      <DamLayout>
        <ManagementLayout
          tableEntity={EntityType.Metadata}
          columns={columns}
          listHook={useMetadataList}
          createAction={{
            onClick: () => openModal(ModalId.CreateMetadata),
          }}
          deleteAction={{
            mutation: deleteMetadataMutation,
          }}
          emptyState={{
            icon: RectangleEllipsis,
            title: tEmptyStates('noMetadataFields.title'),
            message: tEmptyStates('noMetadataFields.description'),
            buttonText: tEmptyStates('noMetadataFields.buttonLabel'),
            onClick: () => openModal(ModalId.CreateMetadata),
          }}
        />
      </DamLayout>
    </>
  );
};

export default Metadata;
